<template>
    <div>
        <ts-page-title
            :title="$t('itemDeduction.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('itemDeduction.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <div>
                    <ts-button color="warning" @click="addNew">{{
                        $t('addNew')
                    }}</ts-button>
                </div>
                <div class="tw-justify-end tw-space-x-2">
                    <Input
                        v-model="search"
                        search
                        :placeholder="$t('search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="custom_keyword">
                    {{
                        row.item_custom_rule
                            ? row.item_custom_rule.custom_rule
                            : ''
                    }}
                </template>
                <template slot-scope="{ row }" slot="is_taxable">
                    <span>
                        <Icon
                            v-if="row.is_taxable"
                            type="ios-checkmark text-success"
                            size="30"
                        />
                        <Icon
                            v-else
                            type="ios-close-circle-outline text-danger"
                            size="20"
                        />
                    </span>
                </template>
                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip
                        confirm
                        :title="$t('areYouSure')"
                        @on-ok="onDelete(row)"
                        :transfer="true"
                        :ok-text="$t('yes')"
                        :cancel-text="$t('no')"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._deleting"
                            ></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                    </Poptip>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('itemDeduction.pageTitle')"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import FromAction from './form.vue'
import { mapState } from 'vuex'
import { debounce } from 'lodash'

export default {
    name: 'item-deduction',
    components: {
        FromAction
    },
    data () {
        return {
            loading: false,
            width: 'width: 300px',
            showForm: false
        }
    },
    computed: {
        ...mapState('payroll/itemDeduction', ['resources', 'pagination']),
        search: {
            get () {
                return this.$store.state.payroll.itemDeduction.search
            },
            set (newValue) {
                this.$store.commit('payroll/itemDeduction/SET_SEARCH', newValue)
                this.$store.commit('payrollI/itemDeduction/RESET_CURRENT_PAGE')
            }
        },

        columns () {
            return [
                {
                    title: this.$t('itemDeduction.deductionItem'),
                    key: 'deduction_item',
                    sortable: true
                },
                {
                    title: this.$t('itemDeduction.customRule'),
                    slot: 'custom_keyword',
                    sortable: true
                },
                {
                    title: this.$t('itemDeduction.taxable'),
                    key: 'is_taxable',
                    slot: 'is_taxable',
                    align: 'center',
                    sortable: true
                },
                {
                    title: this.$t('itemDeduction.description'),
                    key: 'description',
                    sortable: true
                },
                {
                    title: this.$t('actions'),
                    key: 'action',
                    slot: 'action',
                    align: 'center',
                    fixed: 'right',
                    className: 'tw-space-x-2',
                    width: 90
                }
            ]
        }
    },
    methods: {
        addNew () {
            this.showForm = true
            this.$refs.form_action.fetchResource()
            this.$refs.form_action.clearInput()
        },
        clearEdit () {
            this.showForm = false
            this.$store.commit('payroll/itemDeduction/SET_EDIT_DATA', {})
        },
        fetchData (attributes) {
            this.loading = true
            this.$store
                .dispatch('payroll/itemDeduction/fetch', attributes)
                .catch(error => {
                    this.loading = false
                    this.notice({
                        type: 'error',
                        text: error.message
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },
        onEdit (record) {
            this.showForm = true
            this.$store.commit('payroll/itemDeduction/SET_EDIT_DATA', record)
            this.$refs.form_action.setEditData()
            this.$refs.form_action.fetchResource()
        },
        onDelete (record) {
            record._deleting = true
            this.$store
                .dispatch('payroll/itemDeduction/destroy', record.deduction_id )
                .then(response => {
                    this.fetchData()
                    record._deleting = false
                    this.notice({
                        type: response.message_id == 0 ? 'success' : 'warning',
                        text: response.message
                    })
                })
                .catch(error => {
                    this.notice({ type: 'error', text: error.message })
                    record._deleting = false
                })
        },
        notice (not) {
            this.$Notice[not.type]({
                title: 'ITEM DEDUCTION',
                desc: not.text
            })
        }
    },
    watch: {
        search: debounce(function (search) {
            this.fetchData({
                search
            })
        }, 500)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.fetchData()
        })
    }
}
</script>
